/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { Grid, Typography, makeStyles } from '@material-ui/core';
import ToggleOnIcon from '@material-ui/icons/ToggleOn';
import { useParams } from 'react-router';
import PermDataSettingIcon from '@material-ui/icons/PermDataSetting';
import TuneIcon from '@material-ui/icons/Tune';
import SyncAltIcon from '@material-ui/icons/SyncAlt';
import EditIcon from '@material-ui/icons/Edit';
import i18n from 'i18n';
import ApplicationState from 'store/interfaces';
import ViewIcon from '@material-ui/icons/Visibility';
import { connect } from 'react-redux';
import { ModalFields } from 'modules/modal/interfaces';
import { modalOpen } from 'modules/modal/actions';
import { EnvironmentFields } from 'modules/environments/interfaces';
import { getBulkClientsRequest } from 'modules/environments/actions';
import { useTableFilters } from 'hooks/useTableFilters';
import styles, { overrideModal } from './styles';
import TitleBehavior from '../FormBehavior/TitleBehavior';
import EmailAdmin from './EmailAdmin';
import VirtualizerDetails from './VirtualizerDetails';
import LimitLicense from './LimitLicense';
import ConnectionType from './ConnectionType';
import CopyAdmin from './CopyAdmin';
import BlockMultipleSessions from './BlockMultipleSessions';
import UseIOSDevice from './UseIOSDevice';
import ActivateMFA from './ActivateMFA';
import EnvironmentEdit from './Environment';
import VirtualizerEdit from './VirtualizerDetails/VirtualizerEdit';
import CopyAdminToggle from './CopyAdmin/CopyAdminToggle';
import BlockMultipleSessionsToggle from './BlockMultipleSessions/BlockMultipleSessionsToggle';
import UseIOSToggle from './UseIOSDevice/UseIOSToggle';
import ActivateMFAToggle from './ActivateMFA/ActivateMFAToggle';
import EmailAdminEdit from './EmailAdmin/EmailAdminEdit';
import ConnectionTypeConfig from './ConnectionType/ConnectionTypeConfig';
import LimitLicensesConfig from './LimitLicense/LimitLicensesConfig';

const useStyles = makeStyles(styles);

export const initialQuery = {
  offset: 0,
  limit: 10,
  search: '',
};

interface Props {
  environmentSelected: EnvironmentFields | null;
  modalState: ModalFields | null;
  getBulkClientsRequest: (props) => void;
  modalOpen: (props: ModalFields) => void;
}

const BulkClients = ({
  environmentSelected,
  modalState,
  getBulkClientsRequest,
  modalOpen,
}: Props) => {
  const classes = useStyles();
  const { query, searchValue, setSearchValue } = useTableFilters(initialQuery);
  const { id } = useParams<{ id: string }>();
  const [verificationCode, setVerificationCode] = React.useState<string[]>(
    Array(6).fill('')
  );

  const verificationCodeRef = React.useRef(verificationCode);

  React.useEffect(() => {
    verificationCodeRef.current = verificationCode;
  }, [verificationCode]);

  const getBulkClients = () => {
    getBulkClientsRequest({
      id,
      query: { ...query, search: searchValue },
    });
  };

  React.useEffect(() => {
    getBulkClients();
    setSearchValue('');
  }, [
    getBulkClientsRequest,
    query,
    modalState?.modalState === true && modalState,
  ]);

  const handleOpenEmailAdmin = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'md',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.EMAIL_ADMIN_KEY')} - ${i18n.t(
        'BULK_CLIENTS.DETAILS_KEY'
      )}`,
      content: <EmailAdmin environment={environmentSelected} />,
    });
  };

  const handleOpenLimitLicenseDetails = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'md',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.LIMIT_LICENSES_KEY')} - ${i18n.t(
        'BULK_CLIENTS.DETAILS_KEY'
      )}`,
      content: <LimitLicense environment={environmentSelected} />,
    });
  };

  const handleToggleCopyAdminDetails = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'md',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.COPY_EMAIL_ADMIN_KEY')} - ${i18n.t(
        'BULK_CLIENTS.ENABLE_DISABLE_KEY'
      )}`,
      content: (
        <CopyAdminToggle
          setVerificationCode={setVerificationCode}
          verificationCodeRef={verificationCodeRef}
        />
      ),
    });
  };

  const handleToggleBlockMultipleSessions = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'md',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.BLOCK_MULTIPLE_SESSIONS_KEY')} - ${i18n.t(
        'BULK_CLIENTS.ENABLE_DISABLE_KEY'
      )}`,
      content: (
        <BlockMultipleSessionsToggle
          setVerificationCode={setVerificationCode}
          verificationCodeRef={verificationCodeRef}
        />
      ),
    });
  };

  const handleToggleUseIOS = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'md',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.IOS_DEVICES_KEY')} - ${i18n.t(
        'BULK_CLIENTS.ENABLE_DISABLE_KEY'
      )}`,
      content: (
        <UseIOSToggle
          setVerificationCode={setVerificationCode}
          verificationCodeRef={verificationCodeRef}
        />
      ),
    });
  };

  const handleToggleActivateMFA = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'md',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.ACTIVATE_MFA_KEY')} - ${i18n.t(
        'BULK_CLIENTS.ENABLE_DISABLE_KEY'
      )}`,
      content: (
        <ActivateMFAToggle
          setVerificationCode={setVerificationCode}
          verificationCodeRef={verificationCodeRef}
        />
      ),
    });
  };

  const handleToggleConnectionTypeDetails = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'md',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.CONNECTION_TYPE_KEY')} - ${i18n.t(
        'BULK_CLIENTS.CHANGE_CONNECTION_TYPE_KEY'
      )}`,
      content: (
        <ConnectionTypeConfig
          setVerificationCode={setVerificationCode}
          verificationCodeRef={verificationCodeRef}
          environment={environmentSelected}
        />
      ),
    });
  };

  const handleLimitLicensesConfig = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'md',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.LIMIT_LICENSES_KEY')} - ${i18n.t(
        'BULK_CLIENTS.LIMIT_CONFIG_KEY'
      )}`,
      content: (
        <LimitLicensesConfig
          setVerificationCode={setVerificationCode}
          verificationCodeRef={verificationCodeRef}
          environment={environmentSelected}
        />
      ),
    });
  };

  const handleOpenConnectionTypeDetails = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'md',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.CONNECTION_TYPE_KEY')} - ${i18n.t(
        'BULK_CLIENTS.DETAILS_KEY'
      )}`,
      content: <ConnectionType environment={environmentSelected} />,
    });
  };

  const handleOpenVirtualizerDetails = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'lg',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.VIRTUALIZER_KEY')} - ${i18n.t(
        'BULK_CLIENTS.DETAILS_KEY'
      )}`,
      content: <VirtualizerDetails environment={environmentSelected} />,
    });
  };

  const handleOpenVirtualizerEdit = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'lg',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.VIRTUALIZER_KEY')} - ${i18n.t(
        'BULK_CLIENTS.EDIT_KEY'
      )}`,
      content: (
        <VirtualizerEdit
          setVerificationCode={setVerificationCode}
          verificationCodeRef={verificationCodeRef}
          environment={environmentSelected}
        />
      ),
    });
  };

  const handleOpenEmailAdminEdit = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'lg',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.EMAIL_ADMIN_KEY')} - ${i18n.t(
        'BULK_CLIENTS.EDIT_KEY'
      )}`,
      content: (
        <EmailAdminEdit
          setVerificationCode={setVerificationCode}
          verificationCodeRef={verificationCodeRef}
        />
      ),
    });
  };

  const handleOpenCopyAdminDetails = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'md',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.COPY_EMAIL_ADMIN_KEY')} - ${i18n.t(
        'BULK_CLIENTS.DETAILS_KEY'
      )}`,
      content: <CopyAdmin environment={environmentSelected} />,
    });
  };

  const handleOpenBlockMultipleSessionsDetails = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'md',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.BLOCK_MULTIPLE_SESSIONS_KEY')} - ${i18n.t(
        'BULK_CLIENTS.DETAILS_KEY'
      )}`,
      content: <BlockMultipleSessions environment={environmentSelected} />,
    });
  };

  const handleOpenIOSDeviceDetails = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'md',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.IOS_DEVICES_KEY')} - ${i18n.t(
        'BULK_CLIENTS.DETAILS_KEY'
      )}`,
      content: <UseIOSDevice environment={environmentSelected} />,
    });
  };

  const handleOpenActivateMFADetails = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'md',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.ACTIVATE_MFA_KEY')} - ${i18n.t(
        'BULK_CLIENTS.DETAILS_KEY'
      )}`,
      content: <ActivateMFA environment={environmentSelected} />,
    });
  };

  const handleOpenEditEnvironment = () => {
    modalOpen({
      fullWidth: true,
      maxWidth: 'md',
      overrideClasses: overrideModal,
      title: `${i18n.t('BULK_CLIENTS.ENVIRONMENT_KEY')} - ${i18n.t(
        'BULK_CLIENTS.EDIT_KEY'
      )}`,
      content: (
        <EnvironmentEdit
          setVerificationCode={setVerificationCode}
          verificationCodeRef={verificationCodeRef}
          environment={environmentSelected}
        />
      ),
    });
  };

  const cards = [
    {
      title: 'BULK_CLIENTS.ENVIRONMENT_KEY',
      options: [
        {
          title: 'BULK_CLIENTS.MIGRATE_CLIENTS_BETWEEN_ENVIRONMENTS_KEY',
          action: () => handleOpenEditEnvironment(),
          icon: <SyncAltIcon className={classes.publishScheduleIcon} />,
        },
      ],
    },
    {
      title: 'BULK_CLIENTS.EMAIL_ADMIN_KEY',
      options: [
        {
          title: 'BULK_CLIENTS.DETAILS_KEY',
          action: () => handleOpenEmailAdmin(),
          icon: <ViewIcon className={classes.publishScheduleIcon} />,
        },
        {
          title: 'BULK_CLIENTS.EDIT_KEY',
          action: () => handleOpenEmailAdminEdit(),
          icon: <EditIcon className={classes.publishScheduleIcon} />,
        },
      ],
    },
    {
      title: 'BULK_CLIENTS.COPY_EMAIL_ADMIN_KEY',
      options: [
        {
          title: 'BULK_CLIENTS.DETAILS_KEY',
          action: () => handleOpenCopyAdminDetails(),
          icon: <ViewIcon className={classes.publishScheduleIcon} />,
        },
        {
          title: 'BULK_CLIENTS.ENABLE_DISABLE_KEY',
          action: () => handleToggleCopyAdminDetails(),
          icon: <ToggleOnIcon className={classes.publishScheduleIcon} />,
        },
      ],
    },
    {
      title: 'BULK_CLIENTS.BLOCK_MULTIPLE_SESSIONS_KEY',
      options: [
        {
          title: 'BULK_CLIENTS.DETAILS_KEY',
          action: () => handleOpenBlockMultipleSessionsDetails(),
          icon: <ViewIcon className={classes.publishScheduleIcon} />,
        },
        {
          title: 'BULK_CLIENTS.ENABLE_DISABLE_KEY',
          action: () => handleToggleBlockMultipleSessions(),
          icon: <ToggleOnIcon className={classes.publishScheduleIcon} />,
        },
      ],
    },
    {
      title: 'BULK_CLIENTS.IOS_DEVICES_KEY',
      options: [
        {
          title: 'BULK_CLIENTS.DETAILS_KEY',
          action: () => handleOpenIOSDeviceDetails(),
          icon: <ViewIcon className={classes.publishScheduleIcon} />,
        },
        {
          title: 'BULK_CLIENTS.ENABLE_DISABLE_KEY',
          action: () => handleToggleUseIOS(),
          icon: <ToggleOnIcon className={classes.publishScheduleIcon} />,
        },
      ],
    },
    {
      title: 'BULK_CLIENTS.ACTIVATE_MFA_KEY',
      options: [
        {
          title: 'BULK_CLIENTS.DETAILS_KEY',
          action: () => handleOpenActivateMFADetails(),
          icon: <ViewIcon className={classes.publishScheduleIcon} />,
        },
        {
          title: 'BULK_CLIENTS.ENABLE_DISABLE_KEY',
          action: () => handleToggleActivateMFA(),
          icon: <ToggleOnIcon className={classes.publishScheduleIcon} />,
        },
      ],
    },
    {
      title: 'BULK_CLIENTS.LIMIT_LICENSES_KEY',
      options: [
        {
          title: 'BULK_CLIENTS.DETAILS_KEY',
          action: () => handleOpenLimitLicenseDetails(),
          icon: <ViewIcon className={classes.publishScheduleIcon} />,
        },
        {
          title: 'BULK_CLIENTS.LIMIT_CONFIG_KEY',
          action: () => handleLimitLicensesConfig(),
          icon: <TuneIcon className={classes.publishScheduleIcon} />,
        },
      ],
    },
    {
      title: 'BULK_CLIENTS.VIRTUALIZER_KEY',
      options: [
        {
          title: 'BULK_CLIENTS.DETAILS_KEY',
          action: () => handleOpenVirtualizerDetails(),
          icon: <ViewIcon className={classes.publishScheduleIcon} />,
        },
        {
          title: 'BULK_CLIENTS.EDIT_KEY',
          action: () => handleOpenVirtualizerEdit(),
          icon: <EditIcon className={classes.publishScheduleIcon} />,
        },
      ],
    },
    {
      title: 'BULK_CLIENTS.CONNECTION_TYPE_KEY',
      options: [
        {
          title: 'BULK_CLIENTS.DETAILS_KEY',
          action: () => handleOpenConnectionTypeDetails(),
          icon: <ViewIcon className={classes.publishScheduleIcon} />,
        },
        {
          title: 'BULK_CLIENTS.CHANGE_CONNECTION_TYPE_KEY',
          action: () => handleToggleConnectionTypeDetails(),
          icon: <PermDataSettingIcon className={classes.publishScheduleIcon} />,
          hidden: !environmentSelected?.user_connection
            .allow_client_connection_type,
        },
      ].filter(item => !item?.hidden),
    },
  ];

  return (
    <>
      <TitleBehavior
        title={i18n.t('BULK_CLIENTS.CLIENTS_TITLE_KEY')}
        subtitle={i18n.t('BULK_CLIENTS.CLIENTS_SUBTITLE_KEY')}
      />
      <Grid container>
        {cards.map(item => (
          <Grid
            key={item?.title}
            container
            justify={'space-between'}
            className={classes.resourcesCard}
          >
            <Grid item className={classes.cardGridTitle}>
              <Typography className={classes.cardTitle}>
                {i18n.t(item?.title)}
              </Typography>
            </Grid>
            <Grid item className={classes.options}>
              {item?.options.map(item => (
                <Grid
                  key={item?.title}
                  item
                  className={classes.publishSchedule}
                  onClick={item?.action}
                >
                  {item?.icon}
                  <span>{i18n.t(item?.title)}</span>
                </Grid>
              ))}
            </Grid>
          </Grid>
        ))}
      </Grid>
    </>
  );
};

const mapStateToProps = ({
  environments: { environmentSelected },
  modal: { modalState },
}: ApplicationState) => ({
  environmentSelected,
  modalState,
});

export default connect(mapStateToProps, {
  modalOpen,
  getBulkClientsRequest,
})(BulkClients);
